import React, { useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import FactoryScene from './FactoryScene';
import './VisualizationSection.css';

const VisualizationSection = () => {
  // State for PLC data
  const [temp, setTemp] = useState(75);
  const [pressure, setPressure] = useState(4.0);
  const [speed, setSpeed] = useState(45);
  
  // State for animation
  const [isGraphVisible, setIsGraphVisible] = useState(false);
  
  // Simulate changing data
  useEffect(() => {
    const interval = setInterval(() => {
      setTemp(75 + Math.sin(Date.now() * 0.001) * 5);
      setPressure(4 + Math.sin(Date.now() * 0.0007) * 0.5);
      setSpeed(40 + Math.sin(Date.now() * 0.0003) * 10);
    }, 100);
    
    // Show graph after a delay
    const timer = setTimeout(() => {
      setIsGraphVisible(true);
    }, 1000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, []);
  
  return (
    <section className="visualization-section" id="visualization">
      <div className="container">
        <h2 className="section-title">Cyfrowy bliźniak wspierany sztuczną inteligencją LLM</h2>
        <p className="section-subtitle">
          MINERVA tworzy wirtualną kopię Twojego zakładu produkcyjnego wykorzystując duże modele językowe (LLM), 
          umożliwiając inteligentną optymalizację procesów, prewencyjne utrzymanie ruchu i zwiększenie rentowności
        </p>
        
        <div className="visualization-container">
          <div className="visualization-wrapper">
            <Canvas
              shadows
              camera={{ position: [8, 6, 10], fov: 45 }}
              className="visualization-canvas"
              style={{ height: "800px" }}
            >
              <color attach="background" args={['#0f172a']} />
              <Environment preset="city" />
              <FactoryScene 
                tempData={temp}
                pressureData={pressure}
                speedData={speed}
              />
              <OrbitControls 
                makeDefault 
                enableDamping 
                dampingFactor={0.1}
                minDistance={5}
                maxDistance={20}
              />
            </Canvas>
            
            <div className="visualization-card status-card">
              <div className="status-header">
                <h3>Stan procesu</h3>
                <span className="status-indicator online">Online</span>
              </div>
              
              <div className="status-parameter">
                <div className="parameter-label">Temperatura</div>
                <div className="parameter-value-wrapper">
                  <div className="parameter-value">{Math.floor(temp)}°C</div>
                  <div className="parameter-graph">
                    <div className="graph-bar" style={{
                      height: `${isGraphVisible ? (temp - 60) * 2 : 0}%`,
                      backgroundColor: temp > 80 ? '#ef4444' : temp > 75 ? '#f97316' : '#10b981'
                    }}></div>
                  </div>
                </div>
              </div>
              
              <div className="status-parameter">
                <div className="parameter-label">Ciśnienie</div>
                <div className="parameter-value-wrapper">
                  <div className="parameter-value">{pressure.toFixed(1)} bar</div>
                  <div className="parameter-graph">
                    <div className="graph-bar" style={{
                      height: `${isGraphVisible ? pressure * 15 : 0}%`,
                      backgroundColor: pressure > 4.5 ? '#f97316' : '#3b82f6'
                    }}></div>
                  </div>
                </div>
              </div>
              
              <div className="status-parameter">
                <div className="parameter-label">Prędkość</div>
                <div className="parameter-value-wrapper">
                  <div className="parameter-value">{Math.floor(speed)} m/min</div>
                  <div className="parameter-graph">
                    <div className="graph-bar" style={{
                      height: `${isGraphVisible ? speed : 0}%`,
                      backgroundColor: speed > 47 ? '#f97316' : '#10b981'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="visualization-controls">
            <p>Użyj myszy, aby obrócić i przybliżyć model 3D</p>
          </div>
        </div>
        
        <div className="visualization-features">
          <div className="feature-row">
            <div className="visualization-feature-card">
              <div className="feature-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18"></path>
                  <path d="M18.4 9.6a7.5 7.5 0 0 0-9.8 9.8"></path>
                  <path d="m21 3-6 6"></path>
                  <path d="m21 9-9 9"></path>
                  <path d="m15 15-9 9"></path>
                </svg>
              </div>
              <h3>Wczesne wykrywanie anomalii</h3>
              <p>Automatyczne rozpoznawanie odchyleń parametrów poprzez zaawansowaną analizę LLM, umożliwiające prewencyjne działania serwisowe</p>
            </div>
            
            <div className="visualization-feature-card">
              <div className="feature-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 20v-5h-5"></path>
                  <path d="M14 15l6 6"></path>
                  <path d="M4 20v-5h5"></path>
                  <path d="M10 15l-6 6"></path>
                  <path d="M20 4v5h-5"></path>
                  <path d="M14 9l6-6"></path>
                  <path d="M4 4v5h5"></path>
                  <path d="M10 9L4 3"></path>
                </svg>
              </div>
              <h3>Optymalizacja w czasie rzeczywistym</h3>
              <p>Automatyczne dostosowanie parametrów do zmieniających się warunków</p>
            </div>
            
            <div className="visualization-feature-card">
              <div className="feature-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <h3>Dostęp z dowolnego urządzenia</h3>
              <p>Pełna kontrola przez interfejs webowy, łatwość integracji z istniejącymi systemami</p>
            </div>
          </div>
        </div>
        
        <div className="visualization-cta">
          <a href="#contact" className="button button-primary">Umów prezentację</a>
          <a href="#features" className="button button-secondary">Poznaj korzyści</a>
        </div>
      </div>
    </section>
  );
};

export default VisualizationSection;